import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma } from '../../servicios/programaService';
import { obtenerConfigCalidad, actualizarAvanceCalidad } from '../../servicios/calidadService';
import ConfiguracionCalidad from './ConfiguracionCalidad';
import '../estilos/Calidad.css';

function formatFecha(fecha) {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric',
    timeZone: 'America/Santiago'
  });
}

function calcularEstado(act) {
  const hoy = new Date();
  if ((act.porcentajeAvance ?? 0) === 100) return 'completado';
  if (act.fechaFinBase && new Date(act.fechaFinBase) < hoy) return 'atrasado';
  if ((act.porcentajeAvance ?? 0) > 0) return 'en_progreso';
  return 'pendiente';
}

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  en_progreso: 'En progreso',
  completado: 'Completado',
  atrasado: 'Atrasado'
};

const URGENCIA = { atrasado: 0, en_progreso: 1, pendiente: 2, completado: 3 };
const FECHA_LEJANA = new Date('9999-01-01');

function sortPorUrgencia(acts) {
  return [...acts].sort((a, b) => {
    const ua = URGENCIA[calcularEstado(a)] ?? 2;
    const ub = URGENCIA[calcularEstado(b)] ?? 2;
    if (ua !== ub) return ua - ub;
    // Mismo estado → la fecha más próxima (o más vencida) primero
    const da = a.fechaFinBase ? new Date(a.fechaFinBase) : FECHA_LEJANA;
    const db = b.fechaFinBase ? new Date(b.fechaFinBase) : FECHA_LEJANA;
    return da - db;
  });
}

function FilaActividad({ act, onActualizarActividad }) {
  const estado = calcularEstado(act);
  const esAtrasada = estado === 'atrasado';
  const completado = (act.porcentajeAvance ?? 0) === 100;
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [escribiendoObs, setEscribiendoObs] = useState(false);
  const [textoObs, setTextoObs] = useState('');

  const marcarCompletado = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarActividad(act._id, { porcentajeAvance: 100 });
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const abrirObservacion = () => {
    setTextoObs('');
    setError(null);
    setEscribiendoObs(true);
  };

  const guardarObservacion = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarActividad(act._id, {
        observacionCalidad: textoObs,
        observacionCalidadPendiente: true
      });
      setEscribiendoObs(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <tr className={esAtrasada ? 'atrasada' : ''}>
      <td style={{ fontWeight: 500, maxWidth: 280, color: '#000' }}>
        {act._rutaGrupos?.length > 0 && (
          <div className="calidad-ruta-grupos">
            {act._rutaGrupos.map((n, i) => (
              <span key={i}>
                {i > 0 && <span className="calidad-ruta-sep">›</span>}
                <span className="calidad-ruta-item">{n}</span>
              </span>
            ))}
          </div>
        )}
        {act.nombre}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatFecha(act.fechaInicioBase)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatFecha(act.fechaFinBase)}</td>
      <td>
        <span className={`calidad-estado calidad-estado-${estado}`}>
          {ESTADO_LABEL[estado]}
        </span>
      </td>
      <td style={{ minWidth: 220 }}>
        {escribiendoObs ? (
          <div className="calidad-edit-panel">
            <textarea
              placeholder="Describe la observación…"
              value={textoObs}
              onChange={e => setTextoObs(e.target.value)}
              disabled={guardando}
            />
            <div className="calidad-edit-panel-actions">
              <button className="calidad-btn-secondary" onClick={() => setEscribiendoObs(false)} disabled={guardando}>
                Cancelar
              </button>
              <button className="calidad-btn-primary" onClick={guardarObservacion} disabled={guardando || !textoObs.trim()}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="calidad-btn-editar"
              onClick={marcarCompletado}
              disabled={guardando || completado}
              style={completado ? { background: '#dcfce7', borderColor: '#86efac', color: '#15803d' } : undefined}
            >
              {completado ? '✓ Completado' : guardando ? 'Guardando…' : 'Marcar completado'}
            </button>
            {!completado && (
              <button className="calidad-btn-editar" onClick={abrirObservacion} disabled={guardando}>
                Con observaciones
              </button>
            )}
          </div>
        )}
        {error && <span style={{ color: '#dc2626', fontSize: 11, display: 'block', marginTop: 4 }}>{error}</span>}
      </td>
    </tr>
  );
}

function GrupoSeccion({ grupo, onActualizarActividad }) {
  const [colapsado, setColapsado] = useState(false);

  const stats = useMemo(() => {
    const total = grupo.acts.length;
    const completadas = grupo.acts.filter(a => (a.porcentajeAvance ?? 0) === 100).length;
    const atrasadas = grupo.acts.filter(a => calcularEstado(a) === 'atrasado').length;
    let sumDur = 0, sumAv = 0;
    for (const a of grupo.acts) {
      const dur = a.duracionBase || 1;
      sumDur += dur; sumAv += (a.porcentajeAvance || 0) * dur;
    }
    const pct = sumDur > 0 ? Math.round(sumAv / sumDur) : 0;
    return { total, completadas, atrasadas, pct };
  }, [grupo.acts]);

  return (
    <div className="calidad-grupo">
      <div className="calidad-grupo-header" onClick={() => setColapsado(c => !c)}>
        <span className="calidad-grupo-nombre">{grupo.nombre}</span>
        <div className="calidad-grupo-stats">
          <span className="calidad-chip calidad-chip-total">{stats.total}</span>
          {stats.completadas > 0 && (
            <span className="calidad-chip calidad-chip-ok">{stats.completadas} ok</span>
          )}
          {stats.atrasadas > 0 && (
            <span className="calidad-chip calidad-chip-atrasada">{stats.atrasadas} atrasadas</span>
          )}
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginLeft: 4 }}>
            {stats.pct}%
          </span>
          <span className="calidad-grupo-toggle">{colapsado ? '▶' : '▼'}</span>
        </div>
      </div>

      {!colapsado && (
        <div style={{ overflowX: 'auto' }}>
          <table className="calidad-tabla">
            <thead>
              <tr>
                <th>Partida</th>
                <th>Inicio base</th>
                <th>Fin base</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {grupo.acts.map(act => (
                <FilaActividad key={act._id} act={act} onActualizarActividad={onActualizarActividad} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function MatrizCalidad() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [actividadesCalidad, setActividadesCalidad] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarConfig, setMostrarConfig] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, config] = await Promise.all([obtenerPrograma(), obtenerConfigCalidad()]);
      setDatos(prog);
      setActividadesCalidad(config);
    } catch {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actualizarActividad = useCallback(async (actividadId, cambios) => {
    const actualizada = await actualizarAvanceCalidad(datos.programa._id, actividadId, cambios);
    setDatos(prev => ({
      ...prev,
      actividades: prev.actividades.map(a => a._id === actividadId ? { ...a, ...actualizada } : a)
    }));
  }, [datos.programa]);

  const actividadesSet = useMemo(() => new Set(actividadesCalidad), [actividadesCalidad]);

  // Actividades seleccionadas, agrupadas por sección
  const grupos = useMemo(() => {
    const mapaSeccion = {};
    for (const s of datos.secciones) {
      if (s.wbsCode) mapaSeccion[s.wbsCode] = s;
    }
    // Mapa wbsCode → nombre para todas las actividades (para construir ruta ancestros)
    const mapaAct = {};
    for (const act of datos.actividades) {
      if (act.wbsCode) mapaAct[act.wbsCode] = act.nombre;
    }

    const getRuta = (wbsCode) => {
      const partes = wbsCode.split('.');
      const ruta = [];
      for (let i = 1; i < partes.length; i++) {
        const code = partes.slice(0, i).join('.');
        const nombre = i === 1
          ? mapaSeccion[code]?.nombre
          : mapaAct[code];
        if (nombre) ruta.push(nombre);
      }
      return ruta;
    };

    const mapa = {};
    for (const act of datos.actividades) {
      if (!act.wbsCode || !actividadesSet.has(act.wbsCode) || act.observacionCalidadPendiente) continue;
      const nivel1 = act.wbsCode.split('.')[0];
      if (!mapa[nivel1]) {
        const sec = mapaSeccion[nivel1];
        mapa[nivel1] = { nombre: sec?.nombre || nivel1, orden: sec?.orden ?? 9999, acts: [] };
      }
      mapa[nivel1].acts.push({ ...act, _rutaGrupos: getRuta(act.wbsCode).slice(-3) });
    }
    return Object.values(mapa)
      .sort((a, b) => a.orden - b.orden)
      .map(g => ({ ...g, acts: sortPorUrgencia(g.acts) }));
  }, [datos.actividades, datos.secciones, actividadesSet]);

  if (cargando) {
    return <div className="calidad-loading">Cargando actividades…</div>;
  }

  if (!datos.programa) {
    return (
      <div className="calidad-container">
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>No hay programa de obra importado</h3>
          <p>Importa un programa en el módulo Programa de Obra para usar esta vista.</p>
        </div>
      </div>
    );
  }

  if (actividadesCalidad.length === 0) {
    return (
      <div className="calidad-container">
        {mostrarConfig && (
          <ConfiguracionCalidad
            actividades={datos.actividades}
            secciones={datos.secciones}
            actividadesActuales={actividadesCalidad}
            onGuardado={(nuevas) => { setActividadesCalidad(nuevas); setMostrarConfig(false); }}
            onCerrar={() => setMostrarConfig(false)}
          />
        )}
        <div className="calidad-empty">
          <div className="calidad-empty-icon">🔍</div>
          <h3>Sin partidas configuradas</h3>
          <p>Selecciona qué partidas del programa corresponden a Calidad para poder registrar avances.</p>
          <button className="calidad-btn-primary" onClick={() => setMostrarConfig(true)}>
            Seleccionar partidas de Calidad
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="calidad-container">
      {mostrarConfig && (
        <ConfiguracionCalidad
          actividades={datos.actividades}
          secciones={datos.secciones}
          actividadesActuales={actividadesCalidad}
          onGuardado={(nuevas) => { setActividadesCalidad(nuevas); setMostrarConfig(false); }}
          onCerrar={() => setMostrarConfig(false)}
        />
      )}

      <div className="calidad-header">
        <h2>Revision R0</h2>
        <div className="calidad-header-actions">
          <button className="calidad-btn-secondary" onClick={() => setMostrarConfig(true)}>
            ⚙ Configurar partidas
          </button>
        </div>
      </div>

      {grupos.map(grupo => (
        <GrupoSeccion key={grupo.nombre} grupo={grupo} onActualizarActividad={actualizarActividad} />
      ))}
    </div>
  );
}
